import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiService } from './api.service';
import { Client } from '../models/clients.model';
import {
  SftpMigrationConfig,
  SftpMigrationConfigResult,
  SaveSftpMigrationConfigRequest,
} from '../models/sftp-migration-config.model';

@Injectable({
  providedIn: 'root',
})
export class SftpMigrationConfigService {
  private baseUrl = environment.apiUrl + 'SFTPMigration';
  private readonly clientsEndpoint = 'Clients';

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {}

  getConfigs(
    skip = 0,
    count = 10,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    searchTerm?: string,
  ): Observable<SftpMigrationConfigResult> {
    let params = new HttpParams()
      .set('skip', skip.toString())
      .set('count', count.toString());

    const trimmedSearchTerm = String(searchTerm ?? '').trim();
    if (trimmedSearchTerm) params = params.set('search', trimmedSearchTerm);
    if (sortBy) params = params.set('sortBy', sortBy);
    if (sortDir) params = params.set('sortDir', sortDir);

    return this.http
      .get<any>(`${this.baseUrl}`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        map((response) => {
          const rows = this.extractConfigs(response);
          return {
            configs: rows.map((c: any) => this.mapConfig(c)),
            totalCount: Number(
              response?.totalCount ??
                response?.TotalCount ??
                response?.total ??
                response?.Total ??
                response?.count ??
                response?.Count ??
                rows.length,
            ),
          };
        }),
      );
  }

  addConfig(
    request: SaveSftpMigrationConfigRequest,
  ): Observable<{ success: boolean; config: SftpMigrationConfig }> {
    return this.http
      .post<{ success: boolean; sftpMigrationConfiguration: any }>(
        `${this.baseUrl}`,
        request,
        { headers: this.getHeaders() },
      )
      .pipe(
        map((response) => ({
          success: response.success,
          config: this.mapConfig(response.sftpMigrationConfiguration),
        })),
      );
  }

  updateConfig(
    request: SaveSftpMigrationConfigRequest,
  ): Observable<{ success: boolean; config: SftpMigrationConfig }> {
    return this.http
      .put<{ success: boolean; sftpMigrationConfiguration: any }>(
        `${this.baseUrl}`,
        request,
        { headers: this.getHeaders() },
      )
      .pipe(
        map((response) => ({
          success: response.success,
          config: this.mapConfig(response.sftpMigrationConfiguration),
        })),
      );
  }

  getClients(): Observable<Client[]> {
    return this.apiService
      .getData(this.clientsEndpoint)
      .pipe(
        map((res: any) =>
          Array.isArray(res) ? res : (res?.clients ?? res?.Clients ?? []),
        ),
      );
  }

  private mapConfig(c: any): SftpMigrationConfig {
    return {
      pk: String(c.PK ?? c.pk ?? ''),
      pathId: String(c.PathId ?? c.pathId ?? ''),
      client: String(c.Client ?? c.client ?? ''),
      sourcePrefix: String(c.SourcePrefix ?? c.sourcePrefix ?? ''),
      destinationPrefix: String(c.DestinationPrefix ?? c.destinationPrefix ?? ''),
      enabled: c.Enabled ?? c.enabled ?? false,
      service: String(c.Service ?? c.service ?? ''),
    };
  }

  private extractConfigs(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (!response || typeof response !== 'object') return [];

    if (Array.isArray(response.sftpMigrationConfigurations)) {
      return response.sftpMigrationConfigurations;
    }

    const values = Object.values(response);
    const topLevel = values.find((v) => Array.isArray(v));
    if (topLevel) return topLevel as any[];

    for (const value of values) {
      if (value && typeof value === 'object') {
        const nested = Object.values(value).find((v) => Array.isArray(v));
        if (nested) return nested as any[];
      }
    }
    return [];
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
    });
  }
}
